import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { matchPath } from "react-router-dom";
import { ShellIconName } from "./ShellIcon";

// Menu latéral de l'espace professionnel. Une seule définition, lue par la barre latérale (bureau et tiroir
// mobile) et par le fil d'Ariane de la barre du haut, pour que les deux ne divergent pas.
// Structure : maquette v3 (navGroups) ; conditions d'affichage : celles de SimplX (OrganizationArea.tsx
// d'origine), sans en ajouter ni en retirer.
// DUPLICATION NOMMÉE (ARCHITECTURE-CIBLE ADR-011) : le même menu existe dans notre SPA /stats/,
// 2minutesmax/apps/web-dashboard/src/fusion/nav.ts (buildNav), avec les mêmes groupes, le même ordre et les
// mêmes conditions. Toute modification se fait des deux côtés.

export interface ShellNavItem {
  to: string;
  label: string;
  icon: ShellIconName;
  end?: boolean;
  external?: boolean;
  /** Page de notre SPA (/stats/…) : lien <a> dans le même onglet, rechargement complet (autre application). */
  otherApp?: boolean;
  badge?: number;
  /** Pages hors de son URL qui en dépendent dans le fil d'Ariane (lecture d'un profil partagé : « Mes candidats »). */
  alsoMatches?: string[];
}

export interface ShellNavGroup {
  /** Groupe repliable (petit triangle, demande de Nicolas du 31/10) : clé de son état ouvert / fermé. */
  id?: string;
  /** Replié tant que l'utilisateur ne l'a pas ouvert (sauf si la page affichée en fait partie). */
  closedByDefault?: boolean;
  title?: string;
  badge?: string;
  separated?: boolean;
  items: ShellNavItem[];
}

/** Droits lus sur notre API (/api/auth/capabilities, /api/portal/summary) : null tant qu'ils ne sont pas connus. */
export interface CtoRights {
  stats: boolean;
  admin: boolean;
  portalEnabled: boolean;
  portalToComplete: number;
}

export function useProNav({
  isFreeAccount,
  isAdmin,
  hasActiveProPlus,
  rights,
}: {
  isFreeAccount: boolean;
  isAdmin: boolean;
  hasActiveProPlus: boolean;
  rights: CtoRights | null;
}): ShellNavGroup[] {
  const { t } = useTranslation(["common", "organization", "subscription"]);

  return useMemo(() => {
    const groups: ShellNavGroup[] = [];
    // FREEMIUM : pas d'accueil (OrganizationDashboardPage redirige vers Mes candidats) ni de vidéos.
    groups.push({
      items: [
        ...(isFreeAccount
          ? []
          : [
              {
                to: "/organization",
                label: t("common:sidebar.HOME"),
                icon: "home" as const,
                end: true,
              },
            ]),
        {
          to: "/organization/candidates",
          label: t("common:header.MY_CANDIDATES"),
          icon: "profiles",
          alsoMatches: ["/profile-share/:uid"],
        },
        {
          to: "/organization/invited-candidates",
          label: t("common:header.INVITED_CANDIDATES"),
          icon: "mail",
        },
        ...(isFreeAccount
          ? []
          : [
              {
                to: "/organization/capsules",
                label: t("common:header.MY_LISTINGS"),
                icon: "film" as const,
              },
            ]),
      ],
    });
    // Administration et Admin 2'MAX en bas, repliés par défaut (Nicolas, 31/10 : « menu confus, on voit
    // trop de choses », « la partie Admin doit être placée en bas »).
    const administration: ShellNavGroup | null =
      isAdmin && !isFreeAccount
        ? {
            id: "administration",
            closedByDefault: true,
            title: t("organization:ADMINISTRATION"),
            items: [
              {
                to: "/organization/pro/users",
                label: t("organization:users.PAGE_TITLE"),
                icon: "team",
              },
              {
                to: "/organization/pro/subscription",
                label: t("organization:subscription.PAGE_TITLE"),
                icon: "card",
              },
              {
                to: "/organization/pro/account",
                label: t("organization:pro-account.PAGE_TITLE"),
                icon: "settings",
              },
            ],
          }
        : null;
    // Statistiques et Mon portail : droit lu sur notre API (même règle que l'entrée de stats.2minutesmax.com),
    // rangés dans le groupe PRO + quand il existe (admin d'une organisation PRO + : condition SimplX).
    const ours: ShellNavItem[] = rights?.stats
      ? [
          {
            to: "/stats",
            label: t("common:shell.STATS"),
            icon: "overview",
            otherApp: true,
          },
          ...(rights.portalEnabled
            ? [
                {
                  to: "/stats/portail",
                  label: t("common:shell.PORTAL"),
                  icon: "portal" as const,
                  otherApp: true,
                  badge: rights.portalToComplete,
                },
              ]
            : []),
        ]
      : [];
    if (isAdmin && hasActiveProPlus)
      groups.push({
        id: "pro-plus",
        title: t("subscription:label.support"),
        badge: t("organization:PRO_PLUS_ACTIVE"),
        items: [
          {
            to: "/organization/pro/premium-sponsorship",
            label: t("organization:premium-sponsorship.PAGE_TITLE"),
            icon: "bolt",
          },
          ...ours,
        ],
      });
    else if (ours.length) groups[0].items.push(...ours);
    if (administration) groups.push(administration);
    if (rights?.admin)
      groups.push({
        id: "admin-2max",
        closedByDefault: true,
        title: t("common:shell.ADMIN_2MAX"),
        items: [
          {
            to: "/stats/admin/global",
            label: t("common:shell.ADMIN_GLOBAL"),
            icon: "global",
            otherApp: true,
          },
          {
            to: "/stats/admin/consultations",
            label: t("common:shell.ADMIN_CONSULTATIONS"),
            icon: "table",
            otherApp: true,
          },
          {
            to: "/stats/admin/profils",
            label: t("common:shell.ADMIN_PROFILES"),
            icon: "profiles",
            otherApp: true,
          },
          {
            to: "/stats/admin/partenaires",
            label: t("common:shell.ADMIN_ORGANIZATIONS"),
            icon: "building",
            otherApp: true,
          },
          {
            to: "/stats/admin/forums",
            label: t("common:shell.ADMIN_FORUMS"),
            icon: "forum",
            otherApp: true,
          },
        ],
      });
    // « Mon compte » reste à part, sous les blocs (comme dans SimplX).
    groups.push({
      separated: true,
      items: [
        {
          to: "/organization/account",
          label: t("common:header.MY_ACCOUNT"),
          icon: "user",
        },
      ],
    });
    return groups;
  }, [t, isFreeAccount, isAdmin, hasActiveProPlus, rights]);
}

/** Rubrique et groupe de la page affichée, pour le fil d'Ariane (préfixe d'URL le plus long). */
export function findCurrentItem(groups: ShellNavGroup[], pathname: string) {
  let best: { group: ShellNavGroup; item: ShellNavItem } | null = null;
  for (const group of groups)
    for (const item of group.items) {
      if (item.external || item.otherApp) continue;
      if (
        !matchPath({ path: item.to, end: !!item.end }, pathname) &&
        !item.alsoMatches?.some((path) => matchPath(path, pathname))
      )
        continue;
      if (!best || item.to.length > best.item.to.length) best = { group, item };
    }
  return best;
}
